// File: src/services/AIService.js
// Description: AI service layer for handling AI feature API calls and business logic

import { BASE_URL, AGENTIC_URL, AI_SERVER_EMAIL, AI_SERVER_PASSWORD } from '../config';

class AIService {
  // Centralized method for calling the AI server
  static async callAIServer(agenticName, userInput, userCredentials = null) {
    try {
      const payload = {
        email: userCredentials?.email || AI_SERVER_EMAIL,
        password: userCredentials?.password || AI_SERVER_PASSWORD,
        agentic_name: agenticName,
        user_input: userInput
      };

      console.log(`Calling AI server with agent: ${agenticName}`);
      console.log('AI server payload:', payload);

      const response = await fetch(AGENTIC_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      console.log('AI server response status:', response.status);

      if (!response.ok) {
        const errorData = await response.json();
        console.error('AI server error response:', errorData);
        throw new Error(errorData.detail || `AI server error: ${response.status}`);
      }

      const data = await response.json();
      console.log('AI server success response:', data);
      
      // Handle different response formats
      if (data.result && typeof data.result === 'string') {
        // Direct string response
        return data.result;
      } else if (data.result && data.result.raw) {
        // Try to extract text from nested structure
        try {
          const rawData = JSON.parse(data.result.raw);
          if (rawData.result && rawData.result.advice) {
            // Format the response as simple text with sections
            return this.formatNetworkingResponse(rawData.result);
          }
        } catch (parseError) {
          console.log('Could not parse raw result, returning as-is');
        }
        return data.result.raw || data.result;
      } else if (data.result && data.result.text) {
        // Response with text field
        return data.result.text;
      }
      
      // Fallback: return the result as-is
      return data.result;
    } catch (error) {
      console.error('AI Server Error:', error);
      throw error;
    }
  }

  // Mock AI responses for development - replace with real API calls later
  static mockAIResponses = {
    personalization: {
      tech: {
        priority: ['github', 'stack_overflow', 'linkedin'],
        bio: 'AI and networking expert passionate about creating innovative digital solutions',
        skills: ['AI/ML', 'Networking', 'Innovation']
      },
      marketing: {
        priority: ['linkedin', 'twitter', 'instagram'],
        bio: 'Marketing professional focused on building meaningful connections and brand growth',
        skills: ['Marketing', 'Networking', 'Brand Strategy']
      },
      finance: {
        priority: ['linkedin', 'crunchbase', 'glassdoor'],
        bio: 'Finance expert dedicated to building professional networks and industry insights',
        skills: ['Finance', 'Networking', 'Industry Analysis']
      }
    },
    networking: {
      tips: [
        'Always follow up within 24 hours of meeting someone new',
        'Personalize your outreach messages based on their background',
        'Focus on providing value before asking for anything',
        'Build relationships through consistent, genuine interactions',
        'Use social media to stay connected and engaged'
      ],
      iceBreakers: [
        'What inspired you to get into your current field?',
        'What\'s the most exciting project you\'ve worked on recently?',
        'How do you stay updated with industry trends?',
        'What\'s your biggest professional challenge right now?',
        'What advice would you give to someone starting in your industry?'
      ]
    },
    career: {
      insights: [
        'Your AI/ML background positions you well for emerging tech leadership roles',
        'Consider developing skills in product management to complement your technical expertise',
        'Networking in the AI community could lead to exciting startup opportunities',
        'Your networking platform experience is valuable for enterprise software roles'
      ],
      skills: [
        { name: 'Product Management', priority: 'high', gap: 'medium' },
        { name: 'Business Strategy', priority: 'medium', gap: 'high' },
        { name: 'Team Leadership', priority: 'high', gap: 'low' },
        { name: 'Data Analytics', priority: 'medium', gap: 'low' }
      ]
    }
  };

  // AI Personalization Methods
  static async getPersonalizedContent(viewerProfile, userProfile) {
    try {
      // Build compact textual bios per requirement
      const buildViewerText = (vp) => {
        if (!vp) return 'N/A';
        if (vp.bio && typeof vp.bio === 'string') return vp.bio;
        const parts = [];
        if (vp.job_title) parts.push(vp.job_title);
        if (vp.company) parts.push(`at ${vp.company}`);
        if (vp.industry) parts.push(`in ${vp.industry}`);
        const headline = parts.join(' ');
        const loc = vp.location ? `Based in ${vp.location}.` : '';
        const interests = Array.isArray(vp.interests) && vp.interests.length > 0 ? `Interests: ${vp.interests.join(', ')}.` : '';
        const text = [headline, loc, interests].filter(Boolean).join(' ').trim();
        return text || 'N/A';
      };

      const buildUserText = (up) => {
        if (!up) return 'N/A';
        if (up.bio && typeof up.bio === 'string') return up.bio;
        const parts = [];
        if (up.name) parts.push(up.name);
        if (up.job_title) parts.push(`— ${up.job_title}`);
        if (up.company) parts.push(`at ${up.company}`);
        if (up.industry) parts.push(`(${up.industry})`);
        const headline = parts.join(' ');
        const loc = up.location ? `Based in ${up.location}.` : '';
        const text = [headline, loc].filter(Boolean).join(' ').trim();
        return text || 'N/A';
      };

      const viewerText = buildViewerText(viewerProfile);
      const userText = buildUserText(userProfile);

      // Required user_input format: {  viewer profile: Text1,  UserProfile: Text2 }
      const userInput = JSON.stringify({ 'viewer profile': viewerText, 'UserProfile': userText });
      return await this.callAIServer("Personalized Profile Generator", userInput);
    } catch (error) {
      console.error('Error getting personalized content:', error);
      // Return mock data as fallback
      const viewerIndustry = viewerProfile?.industry || 'tech';
      const personalizationKey = viewerIndustry.toLowerCase();
      
      if (this.mockAIResponses.personalization[personalizationKey]) {
        return this.mockAIResponses.personalization[personalizationKey];
      }
      
      // Default personalization
      return this.mockAIResponses.personalization.tech;
    }
  }

  static async updatePersonalizationRules(userId, rules) {
    try {
      // Mock implementation - replace with real API call
      const response = await fetch(`${BASE_URL}/ai/personalization/rules`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${userId}`
        },
        body: JSON.stringify({ rules })
      });
      
      if (response.ok) {
        return await response.json();
      }
      throw new Error('Failed to update personalization rules');
    } catch (error) {
      console.error('Error updating personalization rules:', error);
      // Return mock success for development
      return { success: true, rules };
    }
  }

  static async getPersonalizationAnalytics(userId) {
    try {
      // Mock implementation - replace with real API call
      const response = await fetch(`${BASE_URL}/ai/personalization/analytics`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${userId}`
        }
      });
      
      if (response.ok) {
        return await response.json();
      }
      throw new Error('Failed to fetch personalization analytics');
    } catch (error) {
      console.error('Error fetching personalization analytics:', error);
      // Return mock data for development
      return {
        totalViews: 156,
        personalizedViews: 89,
        topViewers: [
          { name: 'John Doe', company: 'TechCorp', views: 12 },
          { name: 'Jane Smith', company: 'InnovateLab', views: 8 },
          { name: 'Mike Johnson', company: 'StartupXYZ', views: 6 }
        ],
        contentAdaptations: [
          { type: 'bio', adaptations: 45, success: 0.89 },
          { type: 'skills', adaptations: 32, success: 0.92 },
          { type: 'social_media', adaptations: 67, success: 0.78 }
        ]
      };
    }
  }

  // AI Assistant Methods
  static async getGeneralAssistance(userProfile, userInput) {
    try {
      // Build plain-text bio for the user (fallback to composed text if no bio)
      const buildUserBio = (up) => {
        if (!up) return 'N/A';
        if (up.bio && typeof up.bio === 'string') return up.bio;
        const parts = [];
        if (up.name) parts.push(up.name);
        if (up.jobTitle) parts.push(`— ${up.jobTitle}`);
        if (up.currentCompany || up.company) parts.push(`at ${up.currentCompany || up.company}`);
        if (up.industry) parts.push(`(${up.industry})`);
        const headline = parts.join(' ');
        const loc = up.location ? `Based in ${up.location}.` : '';
        const skills = Array.isArray(up.skills) && up.skills.length > 0 ? `Skills: ${up.skills.join(', ')}.` : '';
        const text = [headline, loc, skills].filter(Boolean).join(' ').trim();
        return text || 'N/A';
      };

      const bioText = buildUserBio(userProfile);
      const userInputWithContext = `User Bio Profile: ${bioText}\nUser Question: ${userInput}`;
      const response = await this.callAIServer("AI Networking Assistant", userInputWithContext);
      
      // If response is a string, return it directly
      if (typeof response === 'string') {
        return response;
      }
      
      // If response has a text field, use that
      if (response && response.text) {
        return response.text;
      }
      
              // Fallback to formatted mock data
        return `🎯 GENERAL ASSISTANCE:
I'm here to help you with various professional and personal development questions.

📋 I CAN HELP WITH:
• Career advice and planning
• Professional development strategies
• Networking and relationship building
• Skill development recommendations
• Industry insights and trends
• Personal branding and online presence
• Work-life balance and productivity
• Problem-solving and decision making

💡 TIPS:
• Be specific about what you need help with
• Share relevant context about your situation
• Ask follow-up questions for deeper insights
• Use me as a thinking partner for complex decisions`;
    } catch (error) {
      console.error('Error getting networking advice:', error);
      // Return formatted mock data as fallback
      return `🎯 GENERAL ASSISTANCE:
I'm here to help you with various professional and personal development questions.

📋 I CAN HELP WITH:
• Career advice and planning
• Professional development strategies
• Networking and relationship building
• Skill development recommendations
• Industry insights and trends
• Personal branding and online presence
• Work-life balance and productivity
• Problem-solving and decision making

💡 TIPS:
• Be specific about what you need help with
• Share relevant context about your situation
• Ask follow-up questions for deeper insights
• Use me as a thinking partner for complex decisions`;
    }
  }

  static async generateCollaborationPitch(userProfile, targetProfile) {
    try {
      const userInput = `User Profile: ${JSON.stringify(userProfile)}\nTarget Profile: ${JSON.stringify(targetProfile)}`;
      const response = await this.callAIServer("AI Networking Assistant", userInput);
      
      // If response is a string, return it directly
      if (typeof response === 'string') {
        return response;
      }
      
      // If response has a text field, use that
      if (response && response.text) {
        return response.text;
      }
      
      // Fallback to formatted mock data
      return `📧 COLLABORATION PITCH:

SUBJECT: Potential Collaboration Opportunity

Hi ${targetProfile.name},

I came across your profile and was impressed by your work in ${targetProfile.industry || 'your field'}. Your experience with ${targetProfile.skills?.[0] || 'your expertise'} aligns perfectly with some exciting projects I'm working on.

I believe we could create something valuable together. Would you be interested in a brief conversation to explore potential collaboration opportunities?

Best regards,
${userProfile.name}

📅 FOLLOW-UP: Send a personalized LinkedIn message within 48 hours`;
    } catch (error) {
      console.error('Error generating collaboration pitch:', error);
      // Return formatted mock data as fallback
      return `📧 COLLABORATION PITCH:

SUBJECT: Potential Collaboration Opportunity

Hi ${targetProfile.name},

I came across your profile and was impressed by your work in ${targetProfile.industry || 'your field'}. Your experience with ${targetProfile.skills?.[0] || 'your expertise'} aligns perfectly with some exciting projects I'm working on.

I believe we could create something valuable together. Would you be interested in a brief conversation to explore potential collaboration opportunities?

Best regards,
${userProfile.name}

📅 FOLLOW-UP: Send a personalized LinkedIn message within 48 hours`;
    }
  }

  // Career Insights Methods
  static async getCareerInsights(userProfile = null) {
    try {
      if (userProfile) {
        const userInput = `User Profile: ${JSON.stringify(userProfile)}`;
        
        const response = await this.callAIServer("Career Insights Agent", userInput);
        
        // Try to parse the response if it's a string
        if (typeof response === 'string') {
          try {
            const parsedResponse = JSON.parse(response);
            return this.formatCareerInsightsResponse(parsedResponse);
          } catch (parseError) {
            return this.formatCareerInsightsResponse(response);
          }
        }
        
        // If response is already an object, format it
        if (typeof response === 'object') {
          return this.formatCareerInsightsResponse(response);
        }
        
        return response;
      }
    } catch (error) {
      console.error('Error fetching career insights:', error);
    }
    
    // Return mock insights for development
    return {
      career_trajectory: [
        { 
          year: 2024, 
          role: 'AI Networking Expert', 
          company: 'TouchConnect',
          confidence: 'high',
          reasoning: 'Based on your current expertise in AI and networking technologies'
        },
        { 
          year: 2025, 
          role: 'Product Manager', 
          company: 'Tech Startup',
          confidence: 'medium',
          reasoning: 'Your technical background combined with growing business acumen'
        },
        { 
          year: 2026, 
          role: 'AI Strategy Director', 
          company: 'Enterprise',
          confidence: 'high',
          reasoning: 'Leadership experience and deep AI knowledge will position you well'
        }
      ],
      skill_gaps: [
        { 
          name: 'Product Management', 
          priority: 'high', 
          gap: 'high',
          currentLevel: 'beginner',
          targetLevel: 'intermediate',
          importance: 'critical',
          recommendedResources: ['Coursera Product Management', 'Product School Courses'],
          estimatedTime: '6 months'
        },
        { 
          name: 'Business Strategy', 
          priority: 'medium', 
          gap: 'medium',
          currentLevel: 'intermediate',
          targetLevel: 'advanced',
          importance: 'important',
          recommendedResources: ['Harvard Business Review', 'Strategy Consulting Books'],
          estimatedTime: '12 months'
        },
        { 
          name: 'Data Analysis', 
          priority: 'low', 
          gap: 'low',
          currentLevel: 'advanced',
          targetLevel: 'expert',
          importance: 'nice-to-have',
          recommendedResources: ['Advanced Statistics Courses', 'Machine Learning Specialization'],
          estimatedTime: '3 months'
        }
      ],
      industry_trends: [
        {
          trend: 'AI-powered networking platforms are growing 300% annually',
          impact: 'positive',
          relevance: 'high',
          action: 'Focus on AI integration in your networking solutions',
          timeline: 'now'
        },
        {
          trend: 'Remote work is increasing demand for digital networking tools',
          impact: 'positive',
          relevance: 'high',
          action: 'Develop remote-first networking features',
          timeline: 'within 1 year'
        },
        {
          trend: 'Personalization is becoming the standard in professional networking',
          impact: 'positive',
          relevance: 'medium',
          action: 'Implement AI-driven personalization algorithms',
          timeline: 'ongoing'
        }
      ],
      next_steps: [
        {
          action: 'Develop product management skills through online courses',
          priority: 'high',
          timeline: '3-6 months',
          resources: ['Coursera', 'Product School', 'Udemy'],
          expectedOutcome: 'Basic product management competency',
          dependencies: [],
          effort: 'medium'
        },
        {
          action: 'Network with AI startup founders and investors',
          priority: 'high',
          timeline: '6 months',
          resources: ['LinkedIn', 'Startup Events', 'AngelList'],
          expectedOutcome: 'Industry connections and mentorship',
          dependencies: [],
          effort: 'high'
        },
        {
          action: 'Build thought leadership through content creation',
          priority: 'medium',
          timeline: 'ongoing',
          resources: ['Medium', 'LinkedIn Articles', 'Technical Blogs'],
          expectedOutcome: 'Industry recognition and credibility',
          dependencies: [],
          effort: 'medium'
        }
      ],
      market_opportunities: [
        {
          opportunity: 'AI Product Manager at Tech Startup',
          industry: 'Technology',
          location: 'Remote/Global',
          timeline: 'within 1 year',
          requirements: ['Product Management Skills', 'AI Knowledge', 'Leadership Experience'],
          potentialReward: 'Competitive salary + equity',
          competition: 'medium'
        }
      ]
    };
  }

  // Skill Gap Analysis Methods
  static async getSkillGapAnalysis(userProfile = null) {
    try {
      if (!userProfile) throw new Error('Missing user profile');

      const userInput = `User Profile: ${JSON.stringify(userProfile)}`;
      const response = await this.callAIServer("Skill Gap Analysis Agent", userInput);

      if (typeof response === 'string') {
        try {
          const parsed = JSON.parse(response);
          return this.formatSkillGapAnalysisResponse(parsed);
        } catch {
          // If string is not JSON, attempt to wrap it
          return this.formatSkillGapAnalysisResponse({ result: response });
        }
      }

      if (typeof response === 'object') {
        return this.formatSkillGapAnalysisResponse(response);
      }

      return response;
    } catch (error) {
      console.error('Error fetching skill gap analysis:', error);
      return this.mockSkillGapAnalysis(userProfile);
    }
  }

  static formatSkillGapAnalysisResponse(raw) {
    // Accept multiple shapes; normalize to a consistent structure
    // Expected normalized shape:
    // {
    //   mapping: [{ skill, level, benchmark, gap }],
    //   prioritizedGaps: [{ skill, urgency, marketDemand, recommendedResources, estimatedTime }],
    //   careerPathScenarios: [{ targetRole, feasibility, steps }],
    //   transferableSkills: [{ skill, transferableTo, applicability, note }]
    // }
    try {
      const data = typeof raw === 'string' ? JSON.parse(raw) : raw;
      const result = data?.result?.raw ? (() => { try { return JSON.parse(data.result.raw); } catch { return data; } })() : data?.result || data;

      const mapping = (result.mapping || result.skill_mapping || []).map((m) => ({
        skill: m.skill || m.name || 'Unknown',
        level: m.level || m.current_level || 'unknown',
        benchmark: m.benchmark || m.industry_benchmark || 'unknown',
        gap: m.gap != null ? m.gap : (m.benchmark && m.level ? (m.benchmark - m.level) : null)
      }));

      const prioritizedGaps = (result.prioritizedGaps || result.gap_detection || result.gaps || []).map((g) => ({
        skill: g.skill || g.name || 'Unknown',
        urgency: (g.urgency || g.priority || 'medium').toLowerCase(),
        marketDemand: (g.marketDemand || g.demand || 'medium').toLowerCase(),
        recommendedResources: g.recommendedResources || g.resources || [],
        estimatedTime: g.estimatedTime || g.time || '—'
      }));

      const careerPathScenarios = (result.careerPathScenarios || result.scenarios || []).map((s) => ({
        targetRole: s.targetRole || s.role || 'Target Role',
        feasibility: s.feasibility || s.confidence || 'medium',
        steps: s.steps || s.plan || []
      }));

      const transferableSkills = (result.transferableSkills || result.transferability || []).map((t) => ({
        skill: t.skill || t.from || 'Skill',
        transferableTo: t.transferableTo || t.to || t.domain || '—',
        applicability: t.applicability || t.fit || 'medium',
        note: t.note || t.reason || ''
      }));

      return { mapping, prioritizedGaps, careerPathScenarios, transferableSkills };
    } catch (e) {
      console.warn('Falling back to mock due to parse error in skill gap analysis:', e);
      return this.mockSkillGapAnalysis();
    }
  }

  static mockSkillGapAnalysis(userProfile = {}) {
    const skills = Array.isArray(userProfile?.skills) && userProfile.skills.length > 0
      ? userProfile.skills
      : ['Networking', 'Communication', 'Python', 'Product Management'];

    return {
      mapping: skills.map((s, idx) => ({
        skill: s,
        level: [2, 3, 4][idx % 3],
        benchmark: [3, 4, 4][idx % 3],
        gap: [1, 1, 0][idx % 3]
      })),
      prioritizedGaps: [
        {
          skill: 'Product Management',
          urgency: 'high',
          marketDemand: 'high',
          recommendedResources: ['Product School', 'Coursera PM Specialization'],
          estimatedTime: '3-6 months'
        },
        {
          skill: 'Data Analysis',
          urgency: 'medium',
          marketDemand: 'high',
          recommendedResources: ['Google Data Analytics', 'Kaggle'],
          estimatedTime: '2-4 months'
        }
      ],
      careerPathScenarios: [
        {
          targetRole: 'AI Product Manager',
          feasibility: 'medium',
          steps: ['Complete PM coursework', 'Ship 1-2 side projects', 'Shadow a PM']
        },
        {
          targetRole: 'Data Product Analyst',
          feasibility: 'high',
          steps: ['Strengthen SQL/EDA', 'Build analytics portfolio', 'Contribute to dashboards']
        }
      ],
      transferableSkills: [
        { skill: 'Project Management', transferableTo: 'Product Management', applicability: 'high', note: 'Planning, stakeholder comms, roadmapping' },
        { skill: 'Networking', transferableTo: 'Business Development', applicability: 'medium', note: 'Outreach, relationship building' }
      ]
    };
  }

  // Resume Builder
  static async buildResume(userProfile = {}, options = {}) {
    try {
      const { jobUrl = '', jobDesc = '', resumeOrSkills = '' } = options || {};

      const profileText = (() => {
        if (!userProfile) return 'N/A';
        if (typeof userProfile.bio === 'string' && userProfile.bio.trim().length > 0) return userProfile.bio.trim();
        try { return JSON.stringify(userProfile); } catch { return String(userProfile); }
      })();

      const parts = [];
      if (jobUrl) parts.push(`Job Ad URL: ${jobUrl}`);
      if (jobDesc) parts.push(`Job Ad Description: ${jobDesc}`);
      if (resumeOrSkills) parts.push(`Resume or Key Skills: ${resumeOrSkills}`);
      parts.push(`User Bio Profile: ${profileText}`);
      parts.push('Task: Rewrite and optimize a professional resume tailored to the job, ATS-ready with quantified achievements.');
      const userInput = parts.join('\n');

      const response = await this.callAIServer("Resume Builder Agent", userInput);

      // Normalize output
      if (typeof response === 'string') {
        return { resumeText: response };
      }
      if (response && typeof response === 'object') {
        // Try to unwrap nested result.raw
        if (response.result && typeof response.result === 'string') {
          return { resumeText: response.result };
        }
        if (response.result && response.result.raw) {
          try {
            const raw = JSON.parse(response.result.raw);
            const text = raw?.result?.resume || raw?.result?.text || raw?.resume || raw?.text || response.result.raw;
            return { resumeText: text };
          } catch {
            return { resumeText: response.result.raw };
          }
        }
        if (response.result && response.result.text) {
          return { resumeText: response.result.text };
        }
        return { resumeText: JSON.stringify(response) };
      }
      return { resumeText: String(response) };
    } catch (error) {
      console.error('Error building resume:', error);
      return { resumeText: 'Unable to generate resume at this time. Please try again later.' };
    }
  }

  // Job-Ad Alignment
  static async alignJobAd(userProfile = {}, options = {}) {
    try {
      const { jobUrl = '', jobDesc = '', resumeOrSkills = '' } = options || {};

      const profileText = (() => {
        if (!userProfile) return 'N/A';
        if (typeof userProfile.bio === 'string' && userProfile.bio.trim().length > 0) return userProfile.bio.trim();
        try { return JSON.stringify(userProfile); } catch { return String(userProfile); }
      })();

      const parts = [];
      if (jobUrl) parts.push(`Job Ad URL: ${jobUrl}`);
      if (jobDesc) parts.push(`Job Description: ${jobDesc}`);
      if (resumeOrSkills) parts.push(`Resume or Key Skills: ${resumeOrSkills}`);
      parts.push(`User Bio Profile: ${profileText}`);
      parts.push('Task: Extract job requirements, compare with resume/skills, identify matched items and gaps, and suggest resume updates.');
      const userInput = parts.join('\n');

      const response = await this.callAIServer("Job-Ad Alignment Agent", userInput);

      // Extract primary formatted text (markdown/plain) for UI rendering
      let alignmentText = '';
      if (typeof response === 'string') {
        alignmentText = response;
      } else if (response && typeof response === 'object') {
        if (response.result && typeof response.result === 'string') alignmentText = response.result;
        else if (response.result && typeof response.result.raw === 'string') alignmentText = response.result.raw;
        else if (response.result && typeof response.result.text === 'string') alignmentText = response.result.text;
      }

      const normalized = this.formatJobAdAlignmentResponse(
        typeof response === 'object' ? response : { result: alignmentText || String(response) }
      );

      return { alignmentText, ...normalized };
    } catch (error) {
      console.error('Error aligning job ad:', error);
      return { requirements: [], matched: [], gaps: [], suggestions: ['Unable to analyze at this time.'] };
    }
  }

  static formatJobAdAlignmentResponse(raw) {
    try {
      const unwrap = (data) => {
        if (data?.result?.raw) {
          try { return JSON.parse(data.result.raw); } catch { return data.result.raw; }
        }
        return data?.result ?? data;
      };
      const content = unwrap(typeof raw === 'string' ? { result: raw } : raw);

      // If the API already returns structured fields
      if (typeof content === 'object' && content && !Array.isArray(content)) {
        const requirements = content.requirements || content.job_requirements || [];
        const matched = content.matched || content.matches || [];
        const gaps = content.gaps || content.missing || [];
        const suggestions = content.suggestions || content.resume_suggestions || [];
        if (requirements.length || matched.length || gaps.length || suggestions.length) {
          return { requirements, matched, gaps, suggestions };
        }
      }

      // Fallback: parse markdown/plain text (handles variants like "1. **Extracted Job Requirements**" etc.)
      const text = typeof content === 'string' ? content : JSON.stringify(content);
      const lines = text.split(/\r?\n/);
      const lower = lines.map(l => l.toLowerCase());

      const findHeader = (predicates) => lower.findIndex(l => predicates.some(p => l.includes(p)));
      const isMajorHeader = (l) => /^\s*\d+\./.test(l) || /^\s*#{1,6}\s/.test(l) || /\*\*/.test(l) && /:\s*$/.test(l);

      const requirementsStart = findHeader(['extracted job requirements', 'job requirements']);
      const comparisonStart = findHeader(['comparison report', 'comparison of skills', 'comparison']);
      const recommendationsStart = findHeader(['recommendations']);

      const nextIndex = (...idxs) => idxs.filter(i => i >= 0).sort((a,b)=>a-b)[0] ?? -1;
      const sliceAfter = (startIdx, endIdx) => {
        const s = startIdx >= 0 ? startIdx + 1 : -1;
        const e = endIdx >= 0 ? endIdx : lines.length;
        return s >= 0 ? lines.slice(s, e) : [];
      };
      const trimBullets = (arr) => arr
        .map(l => l.replace(/^\s*[-*]\s+/, '').replace(/^\s*\d+\.\s+/, '').trim())
        .filter(Boolean);

      // Requirements block: between requirements header and next major header
      let reqEnd = nextIndex(comparisonStart, recommendationsStart);
      if (reqEnd === -1) {
        // fallback: next numbered header after requirementsStart
        const after = lines.slice(requirementsStart + 1);
        const rel = after.findIndex(isMajorHeader);
        reqEnd = rel >= 0 ? requirementsStart + 1 + rel : -1;
      }
      const reqBlock = sliceAfter(requirementsStart, reqEnd);
      const requirements = trimBullets(reqBlock.filter(l => /^\s*[-*]/.test(l)));

      // Comparison block: parse Match: and Gaps: lines (comma-separated)
      const compEnd = nextIndex(recommendationsStart);
      const compBlock = sliceAfter(comparisonStart, compEnd);
      const matchLine = compBlock.find(l => l.toLowerCase().includes('match:')) || '';
      const gapsLine = compBlock.find(l => l.toLowerCase().startsWith('   - gaps:') || l.toLowerCase().includes('gaps:')) || '';
      const matched = matchLine ? matchLine.split(/match:/i)[1].split(/,\s*/).map(s => s.trim()).filter(Boolean) : [];
      const gaps = gapsLine ? gapsLine.split(/gaps:/i)[1].split(/,\s*/).map(s => s.trim()).filter(Boolean) : [];

      // Suggestions from recommendations section (bullets), or single-line text
      const recsBlock = sliceAfter(recommendationsStart, -1);
      let suggestions = trimBullets(recsBlock.filter(l => /^\s*[-*]/.test(l)));
      if (suggestions.length === 0 && recsBlock.length > 0) {
        suggestions = recsBlock.map(l => l.trim()).filter(Boolean);
      }

      return { requirements, matched, gaps, suggestions };
    } catch (e) {
      console.warn('Job-Ad alignment parse fallback:', e);
      return { requirements: [], matched: [], gaps: [], suggestions: [] };
    }
  }

  // Cover Letter Generator
  static async generateCoverLetter(userProfile = {}, options = {}) {
    try {
      const { jobUrl = '', jobDesc = '', resumeOrSkills = '', company = '', role = '', highlights = '', tone = 'professional' } = options || {};

      const profileText = (() => {
        if (!userProfile) return 'N/A';
        if (typeof userProfile.bio === 'string' && userProfile.bio.trim().length > 0) return userProfile.bio.trim();
        try { return JSON.stringify(userProfile); } catch { return String(userProfile); }
      })();

      const parts = [];
      if (jobUrl) parts.push(`Job Ad URL: ${jobUrl}`);
      if (jobDesc) parts.push(`Job Description: ${jobDesc}`);
      if (company) parts.push(`Company: ${company}`);
      if (role) parts.push(`Role: ${role}`);
      if (highlights) parts.push(`Resume Highlights: ${highlights}`);
      if (resumeOrSkills) parts.push(`Resume or Key Skills: ${resumeOrSkills}`);
      parts.push(`User Bio Profile: ${profileText}`);
      parts.push(`Tone: ${tone}`);
      parts.push('Task: Generate a tailored cover letter for this application.');
      const userInput = parts.join('\n');

      const response = await this.callAIServer("Career Advisor and Professional Writer", userInput);

      let coverLetterText = '';
      if (typeof response === 'string') {
        coverLetterText = response;
      } else if (response && typeof response === 'object') {
        if (response.result && typeof response.result === 'string') coverLetterText = response.result;
        else if (response.result && typeof response.result.raw === 'string') coverLetterText = response.result.raw;
        else if (response.result && typeof response.result.text === 'string') coverLetterText = response.result.text;
      }

      return { coverLetterText };
    } catch (error) {
      console.error('Error generating cover letter:', error);
      return { coverLetterText: 'Unable to generate cover letter at this time. Please try again later.' };
    }
  }

  // Mock Interview (Job Interview Simulation Agent)
  static async runMockInterview(options = {}) {
    try {
      const { mode = 'practice', chatHistory = [], jobDescription = '', userBio = '', lastUserAnswer = '' } = options || {};

      const lines = [];
      lines.push(`mode: ${mode}`);
      lines.push('chat_history:');
      chatHistory.forEach(msg => {
        const role = (msg.role || '').toUpperCase();
        const text = typeof msg.text === 'string' ? msg.text : '';
        lines.push(`${role}: ${text}`);
      });
      lines.push('job_description:');
      lines.push(jobDescription || '');
      lines.push('user_bio:');
      lines.push(userBio || '');
      lines.push(`last_user_answer: ${lastUserAnswer || ''}`);
      const userInput = lines.join('\n');

      const response = await this.callAIServer("Job Interview Simulation Agent", userInput);

      let mockText = '';
      if (typeof response === 'string') {
        mockText = response;
      } else if (response && typeof response === 'object') {
        if (response.result && typeof response.result === 'string') mockText = response.result;
        else if (response.result && typeof response.result.raw === 'string') mockText = response.result.raw;
        else if (response.result && typeof response.result.text === 'string') mockText = response.result.text;
      }

      return { mockText };
    } catch (error) {
      console.error('Error running mock interview:', error);
      return { mockText: '' };
    }
  }

  static async updateCareerGoals(userId, goals) {
    try {
      const userInput = `User ID: ${userId}\nCareer Goals: ${JSON.stringify(goals)}`;
      return await this.callAIServer("Career Goals Manager", userInput);
    } catch (error) {
      console.error('Error updating career goals:', error);
    }
    
    // Return mock success for development
    return { success: true, goals };
  }

  // Format complex networking response into simple text with sections
  static formatNetworkingResponse(data) {
    let formattedText = '';
    
    if (data.advice) {
      formattedText += `🎯 MAIN ADVICE:\n${data.advice}\n\n`;
    }
    
    if (data.networking_strategies && data.networking_strategies.length > 0) {
      formattedText += `📋 STRATEGIES:\n`;
      data.networking_strategies.forEach(strategy => {
        formattedText += `• ${strategy}\n`;
      });
      formattedText += '\n';
    }
    
    if (data.ice_breakers && data.ice_breakers.length > 0) {
      formattedText += `💡 ICE BREAKERS:\n`;
      data.ice_breakers.forEach(breaker => {
        formattedText += `• ${breaker}\n`;
      });
      formattedText += '\n';
    }
    
    if (data.follow_up_suggestions && data.follow_up_suggestions.length > 0) {
      formattedText += `📅 FOLLOW-UP ACTIONS:\n`;
      data.follow_up_suggestions.forEach(action => {
        formattedText += `• ${action}\n`;
      });
      formattedText += '\n';
    }
    
    if (data.priority_actions && data.priority_actions.length > 0) {
      formattedText += `⚡ PRIORITY ACTIONS:\n`;
      data.priority_actions.forEach(action => {
        formattedText += `• ${action}\n`;
      });
      formattedText += '\n';
    }
    
    if (data.success_metrics) {
      formattedText += `🎯 SUCCESS METRICS:\n`;
      if (data.success_metrics.target_connections) {
        formattedText += `• Target: ${data.success_metrics.target_connections} connections\n`;
      }
      if (data.success_metrics.timeline) {
        formattedText += `• Timeline: ${data.success_metrics.timeline}\n`;
      }
      if (data.success_metrics.expected_outcomes) {
        formattedText += `• Expected Outcomes:\n`;
        data.success_metrics.expected_outcomes.forEach(outcome => {
          formattedText += `  - ${outcome}\n`;
        });
      }
    }
    
    return formattedText.trim();
  }

  // Format career insights response from AI server
  static formatCareerInsightsResponse(data) {
    // If data is a string, try to parse it
    if (typeof data === 'string') {
      try {
        data = JSON.parse(data);
      } catch (error) {
        return this.getDefaultCareerInsights();
      }
    }
    
    // Handle the specific response structure with result.raw (priority)
    if (data.result && data.result.raw && typeof data.result.raw === 'string') {
      try {
        const parsedData = JSON.parse(data.result.raw);
        data = parsedData;
      } catch (error) {
        return this.getDefaultCareerInsights();
      }
    }
    // Handle other response structures
    else if (data.result && typeof data.result === 'string') {
      try {
        data = JSON.parse(data.result);
      } catch (error) {
        return this.getDefaultCareerInsights();
      }
    }
    
    // Extract and format the career insights data
    const formattedInsights = {
      trajectory: this.extractTrajectory(data),
      skillGaps: this.extractSkillGaps(data),
      industryTrends: this.extractIndustryTrends(data),
      nextSteps: this.extractNextSteps(data),
      marketOpportunities: this.extractMarketOpportunities(data),
      networkingStrategy: this.extractNetworkingStrategy(data),
      learningPath: this.extractLearningPath(data),
      careerGoalsAssessment: this.extractCareerGoalsAssessment(data)
    };
    
    return formattedInsights;
  }
  
  // Extract career trajectory from AI response
  static extractTrajectory(data) {
    if (data.career_trajectory && Array.isArray(data.career_trajectory)) {
      return data.career_trajectory.map(item => ({
        year: item.year || new Date().getFullYear(),
        role: item.role || 'Unknown Role',
        company: item.company || 'Unknown Company',
        confidence: item.confidence || 'medium',
        reasoning: item.reasoning || ''
      }));
    }
    
    if (data.trajectory && Array.isArray(data.trajectory)) {
      return data.trajectory;
    }
    
    return this.getDefaultCareerInsights().trajectory;
  }
  
  // Extract skill gaps from AI response
  static extractSkillGaps(data) {
    if (data.skill_gaps && Array.isArray(data.skill_gaps)) {
      return data.skill_gaps.map(item => ({
        name: item.name || 'Unknown Skill',
        priority: item.priority || 'medium',
        gap: item.gap || 'medium',
        currentLevel: item.currentLevel || 'beginner',
        targetLevel: item.targetLevel || 'intermediate',
        importance: item.importance || 'important',
        recommendedResources: item.recommendedResources || [],
        estimatedTime: item.estimatedTime || '3-6 months'
      }));
    }
    
    if (data.skillGaps && Array.isArray(data.skillGaps)) {
      return data.skillGaps;
    }
    
    return this.getDefaultCareerInsights().skillGaps;
  }
  
  // Extract industry trends from AI response
  static extractIndustryTrends(data) {
    if (data.industry_trends && Array.isArray(data.industry_trends)) {
      return data.industry_trends.map(item => ({
        trend: item.trend || 'Unknown Trend',
        impact: item.impact || 'neutral',
        relevance: item.relevance || 'medium',
        action: item.action || '',
        timeline: item.timeline || 'ongoing'
      }));
    }
    
    if (data.industryTrends && Array.isArray(data.industryTrends)) {
      return data.industryTrends;
    }
    
    return this.getDefaultCareerInsights().industryTrends;
  }
  
  // Extract next steps from AI response
  static extractNextSteps(data) {
    if (data.next_steps && Array.isArray(data.next_steps)) {
      return data.next_steps.map(item => ({
        action: item.action || 'Unknown Action',
        priority: item.priority || 'medium',
        timeline: item.timeline || '3-6 months',
        resources: item.resources || [],
        expectedOutcome: item.expectedOutcome || '',
        dependencies: item.dependencies || [],
        effort: item.effort || 'medium'
      }));
    }
    
    if (data.nextSteps && Array.isArray(data.nextSteps)) {
      return data.nextSteps;
    }
    
    return this.getDefaultCareerInsights().nextSteps;
  }
  
  // Extract market opportunities from AI response
  static extractMarketOpportunities(data) {
    if (data.market_opportunities && Array.isArray(data.market_opportunities)) {
      return data.market_opportunities.map(item => ({
        opportunity: item.opportunity || 'Unknown Opportunity',
        industry: item.industry || 'Unknown Industry',
        location: item.location || 'Remote/Global',
        timeline: item.timeline || 'ongoing',
        requirements: item.requirements || [],
        potentialReward: item.potentialReward || '',
        competition: item.competition || 'medium'
      }));
    }
    
    return [];
  }
  
  // Extract networking strategy from AI response
  static extractNetworkingStrategy(data) {
    if (data.networking_strategy) {
      return {
        targetConnections: data.networking_strategy.targetConnections || [],
        events: data.networking_strategy.events || [],
        platforms: data.networking_strategy.platforms || [],
        approach: data.networking_strategy.approach || '',
        followUp: data.networking_strategy.followUp || ''
      };
    }
    
    return {
      targetConnections: [],
      events: [],
      platforms: [],
      approach: '',
      followUp: ''
    };
  }
  
  // Extract learning path from AI response
  static extractLearningPath(data) {
    if (data.learning_path) {
      return {
        shortTerm: data.learning_path.shortTerm || [],
        mediumTerm: data.learning_path.mediumTerm || [],
        longTerm: data.learning_path.longTerm || [],
        certifications: data.learning_path.certifications || [],
        courses: data.learning_path.courses || [],
        mentorship: data.learning_path.mentorship || ''
      };
    }
    
    return {
      shortTerm: [],
      mediumTerm: [],
      longTerm: [],
      certifications: [],
      courses: [],
      mentorship: ''
    };
  }
  
  // Extract career goals assessment from AI response
  static extractCareerGoalsAssessment(data) {
    if (data.career_goals_assessment) {
      return {
        realisticGoals: data.career_goals_assessment.realisticGoals || [],
        challengingGoals: data.career_goals_assessment.challengingGoals || [],
        timelineAdjustments: data.career_goals_assessment.timelineAdjustments || {},
        prerequisites: data.career_goals_assessment.prerequisites || [],
        riskFactors: data.career_goals_assessment.riskFactors || [],
        alternativePaths: data.career_goals_assessment.alternativePaths || []
      };
    }
    
    return {
      realisticGoals: [],
      challengingGoals: [],
      timelineAdjustments: {},
      prerequisites: [],
      riskFactors: [],
      alternativePaths: []
    };
  }
  
  // Get default career insights when API fails
  static getDefaultCareerInsights() {
    return {
      trajectory: [
        { year: 2024, role: 'AI Networking Expert', company: 'TouchConnect' },
        { year: 2025, role: 'Product Manager', company: 'Tech Startup' },
        { year: 2026, role: 'AI Strategy Director', company: 'Enterprise' }
      ],
      skillGaps: [
        { name: 'Product Management', priority: 'high', gap: 'high' },
        { name: 'Business Strategy', priority: 'medium', gap: 'medium' },
        { name: 'Data Analysis', priority: 'low', gap: 'low' }
      ],
      industryTrends: [
        'AI-powered networking platforms are growing 300% annually',
        'Remote work is increasing demand for digital networking tools',
        'Personalization is becoming the standard in professional networking'
      ],
      nextSteps: [
        'Develop product management skills through online courses',
        'Network with AI startup founders and investors',
        'Build thought leadership through content creation',
        'Consider pursuing an MBA or business certification'
      ]
    };
  }

  // Utility Methods
  static async simulateAIProcessing(delay = 1000) {
    // Simulate AI processing time
    return new Promise(resolve => setTimeout(resolve, delay));
  }

  static generateMockInsight() {
    const insights = [
      'Your networking approach shows strong relationship-building skills',
      'Consider attending more industry-specific events',
      'Your LinkedIn engagement rate is above average',
      'You have strong connections in the tech industry'
    ];
    return insights[Math.floor(Math.random() * insights.length)];
  }

  // Conversations Processor
  static async processConversation(request) {
    // request: { transcript, features: { summarize, actions, followups, sentimentIntent, translations: [langs] }, language: { autoDetect, inputLang, outputLanguages } }
    try {
      const payload = {
        transcript: request?.transcript || '',
        features: request?.features || {},
        language: request?.language || {}
      };

      // Call external Agentic endpoint (placeholder agent name). You will provide exact API later.
      const response = await this.callAIServer('Transcript Processing Agent', JSON.stringify(payload));

      // Normalize diverse shapes
      if (typeof response === 'string') {
        try {
          const obj = JSON.parse(response);
          return this.normalizeConversationResponse(obj);
        } catch {
          return {
            summary: response,
            keyPoints: [],
            actionItems: [],
            followUps: { email: '', linkedin: '', whatsapp: '' },
            translations: {},
            sentiment: { tone: 'neutral' },
            intents: []
          };
        }
      }
      if (typeof response === 'object') {
        // unwrap { result: raw } shapes
        const raw = response?.result?.raw ? (() => { try { return JSON.parse(response.result.raw); } catch { return response.result.raw; } })() : (response?.result || response);
        return this.normalizeConversationResponse(raw);
      }
      return this.normalizeConversationResponse({});
    } catch (e) {
      console.error('processConversation error:', e);
      // Fallback mock
      return {
        summary: 'High-level summary of the discussion with key outcomes.',
        keyPoints: ['Aligned on scope', 'Pricing to be shared', 'Next meeting next week'],
        actionItems: [
          { description: 'Send pricing deck', owner: 'You', dueDate: 'Tuesday', priority: 'high' },
          { description: 'Confirm availability for next call', owner: 'Jane', dueDate: 'Friday', priority: 'medium' }
        ],
        followUps: {
          email: 'Subject: Great meeting — next steps\n\nThanks for the time today... [personalized summary + requested docs].',
          linkedin: 'Great speaking with you — I sent a recap via email. Looking forward to next steps!',
          whatsapp: 'Thanks for today! Sharing the deck shortly. Let’s target Tue for a quick follow-up.'
        },
        translations: {},
        sentiment: { tone: 'positive', score: 0.85 },
        intents: [{ type: 'partnership', confidence: 0.9 }],
        entities: [
          { type: 'person', text: 'Jane', role: 'CTO', email: 'jane@example.com' },
          { type: 'company', text: 'Acme', website: 'https://acme.com' }
        ],
        enrichment: {
          contacts: [
            { name: 'Jane', company: 'Acme', email: 'jane@example.com', website: 'https://acme.com' }
          ]
        }
      };
    }
  }

  static normalizeConversationResponse(raw) {
    try {
      const obj = typeof raw === 'string' ? JSON.parse(raw) : raw || {};
      const summary = obj.summary || obj.overview || '';
      const keyPoints = obj.keyPoints || obj.points || [];
      const actionItems = obj.actionItems || obj.tasks || [];
      const followUps = obj.followUps || obj.followups || { email: '', linkedin: '', whatsapp: '' };
      const translations = obj.translations || obj.localized || {};
      const sentiment = obj.sentiment || { tone: 'neutral' };
      const intents = obj.intents || obj.intent || [];
      const entities = obj.entities || [];
      const enrichment = obj.enrichment || {};
      return { summary, keyPoints, actionItems, followUps, translations, sentiment, intents, entities, enrichment };
    } catch {
      return { summary: '', keyPoints: [], actionItems: [], followUps: { email: '', linkedin: '', whatsapp: '' }, translations: {}, sentiment: { tone: 'neutral' }, intents: [], entities: [], enrichment: {} };
    }
  }
}

export default AIService;

